import React, { useEffect, useRef, useState } from "react";

// UI and custom components
import CardSection from "../../../../../../../components/ui/card/CardSection";
import MolGrid from "../../../../../../../components/molGrid/MolGrid";
import Label from "../../../../../../../components/ui/label/Label";
import Buttons from "../../../../../../../components/ui/button/Buttons";
import DropdownSelect from "../../../../../../../components/forms/ui/inputs/dropdown/DropdownSelect";
import TooltipComponent from "../../../../../../../components/ui/tooltip/TooltipComponent";
// Configs
import { importLiabilitiesDataGridConfig, previewLiabilitiesDataGridConfig, } from "./ImportLiabilitiesData.config";
// API hooks
import { useUploadCsvFileMutation } from "../../../../../../../app/services/uploadCSVAPI";
import { useImportReportFieldsMutation } from "../../../../../../../app/services/clientValuationAPI";
import { useLazyGetassignedReportTemplateQuery } from "../../../../../../../app/services/reportTemplateAPI";
import ToastService from "../../../../../../../services/toastService/ToastService";
import DataLoader from "../../../../../../../components/molGrid/ui/dataLoader/DataLoader";
import { propTypes } from "react-bootstrap/esm/Image";
import PropTypes from "prop-types";

const ImportLiabilities = (props) => {
  const molGridRef = useRef();
  // Local state management
  const [dataSource, setDataSource] = useState([]);
  const [segmentList, setSegmentList] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [fileName, setFileName] = useState("");
  const [csvFileData, setCsvFileData] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [csvHeader, setcsvHeader] = useState([]);
  const [selectRow, setSelectrow] = useState(null);
  const [isImportData, setIsImportData] = useState(false)
  const [sortState, setSortState] = useState([]);


  // Fetch assigned liability data on component mount
  const [GetAssignedReportTemplate, {
    isLoading: isassignedLiabilityBasicFetching,
    isSuccess: isassignedLiabilityBasicFetched,
    data: assignedLiabilityBasicData
  }] = useLazyGetassignedReportTemplateQuery();

  // Upload CSV mutation hook
  const [uploadCSVFile, {
    isLoading: isuploadCSVFileFetching,
    isSuccess: isuploadCSVFileFetched,
    data: uploadCSVFileData
  }] = useUploadCsvFileMutation();

  // ImportReportFields CSV mutation hook
  const [importReportFields, {
    isLoading: isimportReportFieldsFetching,
    isSuccess: isimportReportFieldsFetched,
    data: importReportFieldsData,
    isError: isimportReportFieldsError,
  }] = useImportReportFieldsMutation();

  // Handle file upload change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      uploadCSVFile(file);
    } else {
      setFileName("");
    }
  };

  const sortData = (data, sortState) => {

    return [...data].sort((a, b) => {
      for (const sort of sortState) {
        const valA = a[sort.column] ?? '';
        const valB = b[sort.column] ?? '';
        const comparison = sort.direction === 'asc'
          ? valA.toString().localeCompare(valB.toString())
          : valB.toString().localeCompare(valA.toString());
        if (comparison !== 0) return comparison;
      }
      return 0;
    });
  };

  const handleSortingChange = async (sortString, col) => {
    if (!sortString) return;
    const newSortState = sortString
      .split(',')
      .map(item => {
        const [column, directionRaw] = item.trim().split(/\s+/);
        const direction = directionRaw?.toLowerCase() === 'desc' ? 'desc' : 'asc';
        return { column, direction };
      })
      .filter(item => item.column);
    const filtered = newSortState.filter(item => item.column === col.fieldName);
    setSortState(newSortState);
    const orderData = await sortData(dataSource, filtered);
    setDataSource(orderData);
  };

  // Close modal or parent trigger
  const handleCloseClick = () => {
    props?.onClose();
  };

  // When a segment is selected from dropdown
  const handleSegmentChange = (selected) => {
    const index = selected.value;
    setSelectedSegment(selected);
    setCsvFileData(segmentList[index]);
  };


  const generateJsonCsvData = (rawData) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    const columnLetters = Object.keys(rawData[0]).filter(key => key !== "sno");
    const JsonCsvData = [];
    rawData.forEach((rowData, rowIndex) => {
      const row = rowIndex + 1; // Starting from 2 because row 1 is header
      columnLetters.forEach((colLetter, colIndex) => {
        const col = colIndex + 1;
        const rawValue = rowData[colLetter];
        const value = rawValue !== undefined && rawValue !== null ? rawValue.toString().trim() : "";
        JsonCsvData.push({
          Row: row,
          Col: col,
          Value: value,
        });
      });
    });

    return JsonCsvData;

  }

  const handleImportClick = () => {
    const req = {
      reportTemplateId: props?.ReportTemplateID,
      LiabilityBasisTypeId: selectRow?.liabilityBasisTypeid,
      ValId: props?.valid,
      JsonCsvData: [
        {
          Row: '',
          Col: '',
          Value: '',
        },
      ],
      JsonCsvData: generateJsonCsvData(csvFileData),
    }
    importReportFields(req)
  }


  const handleRowSelect = (rowData) => {
    setSelectrow(rowData);
  };


  const handleViewClick = (data) => {
    setSelectrow(data);
  }


  const actionHandler = {
    VIEW: handleViewClick,
  };

  useEffect(() => {
    if (props?.ReportTemplateID) {
      GetAssignedReportTemplate(props?.ReportTemplateID);
    }
  }, [props?.ReportTemplateID]);

  function chunkArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }
  useEffect(() => {
    if (isuploadCSVFileFetched && uploadCSVFileData) {
      const chunked = chunkArray(uploadCSVFileData?.data, 62);
      setSegmentList(chunked)
      setCsvFileData(chunked[0]);
      setcsvHeader(Object.keys(chunked[0]?.[0] || {}));
      const options = chunked.map((_, idx) => ({
        label: `Val Passes #${idx + 1}`,
        value: idx,
      }));
      setOriginOptions(options);
      setSelectedSegment(options[0])
    }
  }, [isuploadCSVFileFetching, isuploadCSVFileFetched, uploadCSVFileData]);

  useEffect(() => {
    if (isimportReportFieldsFetching) {
      setIsImportData(true);
    }
    if (!isimportReportFieldsFetching && isimportReportFieldsFetched && importReportFieldsData) {
      ToastService.success(importReportFieldsData?.errorMessage)
      setIsImportData(false);
    }
  }, [isimportReportFieldsFetching, isimportReportFieldsFetched, importReportFieldsData])

  useEffect(() => {
    if (isassignedLiabilityBasicFetched && assignedLiabilityBasicData) {
      if (assignedLiabilityBasicData?.length > 0) {
        setDataSource(assignedLiabilityBasicData);
        molGridRef.current.resetSelectedRow(0)
        setSelectrow(assignedLiabilityBasicData[0]);
      }
    }
  }, [isassignedLiabilityBasicFetching, isassignedLiabilityBasicFetched, assignedLiabilityBasicData]);

  return (
    <>
      <div className="row">
        <div className="col-xxl-9 col-xl-9 col-lg-9 col-12 assign-liability-table">
          <CardSection cardTitle="Assign Liability Sets">
            <MolGrid
              ref={molGridRef}
              configuration={importLiabilitiesDataGridConfig}
              dataSource={dataSource}
              allowPagination={false}
              selectRow={selectRow}
              pagination={{
                totalCount: dataSource.length,
                pageSize: 20,
                currentPage: 1,
              }}
              onActionChange={actionHandler}
              onRowSelect={handleRowSelect}
              isLoading={isassignedLiabilityBasicFetching}
              rowDoubleClick={handleViewClick}
              onSorting={handleSortingChange}
            />
          </CardSection>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-12">
          <CardSection cardTitle="CSV File Upload">
            <div className="form-section">
              <div className="custom-file-upload-input">
                <div className="input-label">
                  <Label labelName="Browse CSV File" />
                  <div className="file-input-custom">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      disabled={selectRow != null ? false : true}
                    />
                    <div className="custom-btn-pic">
                      <Buttons
                        buttonText="Browse CSV"
                        buttonTypeClassName="info-btn"
                        isDisable={selectRow != null ? false : true}
                      />
                    </div>
                  </div>
                </div>
                <p>{fileName ? `${fileName}` : ""}</p>
              </div>

              <div className="dropdown-select mt-3">
                <div className="d-flex justify-content-between">
                  <Label labelName="Select Val Pass for Import" />
                  <TooltipComponent
                    iconName="eva:info-fill"
                    message={
                      <>
                        <b>Information About List</b>
                        <ol>
                          <li>Browse CSV File.</li>
                          <li>Listbox will display links once file is uploaded.</li>
                          <li>Click on document links to preview sets.</li>
                          <li>*Max 8 documents supported.</li>
                        </ol>
                      </>
                    }
                  />
                </div>
                <DropdownSelect
                  optionsValue={originOptions}
                  placeholder={"Select Origin Type"}
                  isMultiSelect={false}
                  isDropdownDisabled={false}
                  handleDropdownChange={handleSegmentChange}
                  value={selectedSegment}
                />
              </div>

              <div className="select-val-pass d-flex fle mt-2">
                <p>Selected val Pass:</p>&nbsp;<span>none</span>
              </div>
            </div>
          </CardSection>
        </div>
      </div>

      {isImportData ? <DataLoader message={"Importing liabilities, please wait..."} /> :
        <div className="row">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-12 import-liabilities-list custom-table-scroll">
            <CardSection cardTitle="Preview Selected Val Pass Data">
              {
                csvHeader.length > 0 ? (
                  <div className="table-container">
                    <div className="table-wrapper">
                      <table className=" scrollable-table" style={{ borderCollapse: 'collapse', width: '100%' }} border="1" cellPadding={6}>
                        <thead>
                          <tr>
                            {csvHeader.map((key, index) => (
                              <th key={index + 1}>{key.trim()}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvFileData.map((row, rowIndex) => (
                            <tr key={rowIndex + 1}>
                              {csvHeader.map((key, colIndex) => {
                                const value = row[key];
                                const displayValue = typeof value === 'object' && value !== null
                                  ? JSON.stringify(value)
                                  : value ?? '';
                                return <td key={colIndex + 1}
                                  className={colIndex === 0 ? "first_td" : ""}
                                >{displayValue}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                  </div>
                ) : (
                  <div className="preview-data-result">
                    <MolGrid
                      configuration={previewLiabilitiesDataGridConfig}
                      dataSource={[]}
                      allowPagination={false}
                      pagination={{
                        totalCount: segmentList?.length,
                        pageSize: 20,
                        currentPage: 1,
                      }}
                      onActionChange={actionHandler}
                    />
                  </div>
                )
              }

            </CardSection>
            <div className="mt-3 d-flex justify-content-end">
              <Buttons
                buttonText="Cancel"
                buttonTypeClassName="btn secondary-btn mr-2"
                onClick={handleCloseClick}
              />
              <Buttons
                buttonText="Import Data"
                buttonTypeClassName="blue-btn"
                onClick={handleImportClick}
                isDisable={selectRow && selectedSegment != null ? false : true}
              />
            </div>
          </div>
        </div>}
    </>
  );
};

ImportLiabilities.propTypes = {
  ReportTemplateID: PropTypes.number,
  valid: propTypes.number,
  onClose: PropTypes.func
};

export default ImportLiabilities;
